import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import type VimeoPlayer from '@vimeo/player';
import { useEffect, useRef } from 'react';
import { View } from 'react-native';
import { WebView, type WebViewMessageEvent } from 'react-native-webview';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { BASE_COLORS } from '@/constants/colors';
import { useAbility } from '@/hooks/useAbility';
import { segmentClient } from '@/util/analytics';

const VIDEO_REFERRER = 'https://primum.co';

const getVimeoSrc = (videoId: string | null, autoplay: boolean) => {
  const auto = autoplay ? 1 : 0;

  return `https://player.vimeo.com/video/${videoId}?playsinline=1&autoplay=${auto}&api=1`;
};

const getYouTubeOrigin = () => {
  if (isWeb && typeof window !== 'undefined') {
    return window.location.origin;
  }

  return VIDEO_REFERRER;
};

const getYouTubeSrc = (videoId: string | null, autoplay: boolean) => {
  const auto = autoplay ? 1 : 0;

  const mute = autoplay ? 1 : 0;

  return `https://www.youtube.com/embed/${videoId}?playsinline=1&autoplay=${auto}&mute=${mute}&rel=0&modestbranding=1&enablejsapi=1&origin=${encodeURIComponent(getYouTubeOrigin())}`;
};

export const getVideoUrl = (videoId: string | null, videoProvider: string | null) => {
  if (videoProvider === 'YouTube') {
    return `https://www.youtube.com/watch?v=${videoId}`;
  }

  return `https://vimeo.com/${videoId}`;
};

const getSrc = (videoId: string | null, videoProvider: string | null, autoplay: boolean) => {
  if (videoProvider === 'YouTube') {
    return getYouTubeSrc(videoId, autoplay);
  }

  return getVimeoSrc(videoId, autoplay);
};

/**
 * Generates HTML for native WebView that wraps the video player and listens for play events.
 * Uses postMessage to communicate back to React Native when video starts playing.
 */
const getNativePlayerHtml = (
  videoId: string | null,
  videoProvider: string | null,
  autoplay: boolean
) => {
  if (videoProvider === 'YouTube') {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
          <meta name="referrer" content="strict-origin-when-cross-origin">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            html, body { width: 100%; height: 100%; overflow: hidden; background: #000; }
            #player { width: 100%; height: 100%; }
          </style>
        </head>
        <body>
          <div id="player"></div>
          <script src="https://www.youtube.com/iframe_api"></script>
          <script>
            var player;
            var hasNotifiedPlay = false;

            function onYouTubeIframeAPIReady() {
              player = new YT.Player('player', {
                videoId: '${videoId}',
                playerVars: {
                  playsinline: 1,
                  autoplay: ${autoplay ? 1 : 0},
                  mute: ${autoplay ? 1 : 0},
                  rel: 0,
                  modestbranding: 1
                },
                events: {
                  onStateChange: onPlayerStateChange
                }
              });
            }

            function onPlayerStateChange(event) {
              if (event.data === YT.PlayerState.PLAYING && !hasNotifiedPlay) {
                hasNotifiedPlay = true;
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'play' }));
              }
            }
          </script>
        </body>
      </html>
    `;
  }

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          html, body { width: 100%; height: 100%; overflow: hidden; background: #000; }
          iframe { width: 100%; height: 100%; border: 0; }
        </style>
      </head>
      <body>
        <iframe
          id="vimeo-player"
          src="https://player.vimeo.com/video/${videoId}?playsinline=1&autoplay=${autoplay ? 1 : 0}"
          allow="autoplay; fullscreen; picture-in-picture"
        ></iframe>
        <script src="https://player.vimeo.com/api/player.js"></script>
        <script>
          var hasNotifiedPlay = false;
          var iframe = document.getElementById('vimeo-player');
          var player = new Vimeo.Player(iframe);

          player.on('play', function() {
            if (!hasNotifiedPlay) {
              hasNotifiedPlay = true;
              window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'play' }));
            }
          });
        </script>
      </body>
    </html>
  `;
};

export function VideoPlayer({
  aspectRatio,
  autoplay = false,
  contentLibraryItemId,
  onFirstPlay,
  videoId,
  videoProvider = 'Vimeo',
}: {
  aspectRatio?: {
    height: number;
    width: number;
  };
  autoplay?: boolean;
  contentLibraryItemId?: string;
  onFirstPlay?: () => void;
  videoId: string | null;
  videoProvider: string | null;
}) {
  const canReadContentLibrary = useAbility().can('read', 'ContentLibraryItem');

  const src = getSrc(videoId, videoProvider, autoplay);

  const ratioWidth = aspectRatio?.width ?? 16;

  const ratioHeight = aspectRatio?.height ?? 9;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const hasNotifiedPlayRef = useRef(false);

  const onFirstPlayRef = useRef(onFirstPlay);

  onFirstPlayRef.current = onFirstPlay;

  const shouldTrackPlay = !!contentLibraryItemId || !!onFirstPlay;

  const trackFirstPlay = () => {
    if (hasNotifiedPlayRef.current) {
      return;
    }

    hasNotifiedPlayRef.current = true;

    if (contentLibraryItemId) {
      segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_VIDEO_CLICKED, {
        authenticated: canReadContentLibrary,
        contentLibraryItemId,
        embedded: false,
        videoId,
        videoProvider,
        videoUrl: getVideoUrl(videoId, videoProvider),
      });
    }

    onFirstPlayRef.current?.();
  };

  useEffect(() => {
    if (!isWeb || !shouldTrackPlay || !iframeRef.current) {
      return;
    }

    if (videoProvider !== 'Vimeo') {
      return;
    }

    let player: VimeoPlayer | null = null;

    let cancelled = false;

    const loadVimeoPlayer = async () => {
      const VimeoPlayer = (await import('@vimeo/player')).default;

      if (cancelled || !iframeRef.current) {
        return;
      }

      player = new VimeoPlayer(iframeRef.current);

      player.on('play', () => {
        trackFirstPlay();
      });
    };

    void loadVimeoPlayer();

    return () => {
      cancelled = true;

      if (player) {
        player.off('play');

        player.pause().catch(() => {});
      }
    };
  }, [shouldTrackPlay, videoProvider, src]);

  useEffect(() => {
    if (!isWeb || !shouldTrackPlay || !iframeRef.current) {
      return;
    }

    if (videoProvider !== 'YouTube') {
      return;
    }

    const iframe = iframeRef.current;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ytWindow = window as any;

    let cancelled = false;

    const setupPlayer = () => {
      if (cancelled) {
        return;
      }

      new ytWindow.YT.Player(iframe, {
        events: {
          onStateChange: (event: { data: number }) => {
            if (!cancelled && event.data === 1) {
              trackFirstPlay();
            }
          },
        },
      });
    };

    if (ytWindow.YT?.Player) {
      setupPlayer();
    } else {
      const prevCallback = ytWindow.onYouTubeIframeAPIReady;

      ytWindow.onYouTubeIframeAPIReady = () => {
        prevCallback?.();

        setupPlayer();
      };

      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const script = document.createElement('script');

        script.src = 'https://www.youtube.com/iframe_api';

        document.head.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [shouldTrackPlay, videoProvider, src]);

  useEffect(() => {
    hasNotifiedPlayRef.current = false;
  }, [videoId]);

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    return () => {
      const iframe = iframeRef.current;

      if (iframe) {
        iframe.src = '';
      }
    };
  }, []);

  if (isWeb) {
    return (
      <div
        style={{
          aspectRatio: `${ratioWidth} / ${ratioHeight}`,
          backgroundColor: BASE_COLORS.black,
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
        }}>
        <iframe
          allow="autoplay; fullscreen; picture-in-picture"
          ref={iframeRef}
          src={src}
          style={{
            border: '0',
            height: '100%',
            left: '0',
            position: 'absolute',
            top: '0',
            width: '100%',
          }}
          title="Video player"
        />
      </div>
    );
  }

  const handleNativeMessage = (event: WebViewMessageEvent) => {
    if (!shouldTrackPlay) {
      return;
    }

    try {
      const data = JSON.parse(event.nativeEvent.data);

      if (data.type === 'play') {
        trackFirstPlay();
      }
    } catch {
      // Ignore parse errors
    }
  };

  return (
    <View
      style={{
        aspectRatio: ratioWidth / ratioHeight,
        backgroundColor: '#000',
        overflow: 'hidden',
        width: '100%',
      }}>
      <WebView
        allowsFullscreenVideo
        allowsInlineMediaPlayback
        domStorageEnabled
        javaScriptEnabled
        mediaPlaybackRequiresUserAction={false}
        onMessage={shouldTrackPlay ? handleNativeMessage : undefined}
        originWhitelist={['*']}
        scrollEnabled={false}
        source={
          shouldTrackPlay
            ? {
                baseUrl: VIDEO_REFERRER,
                html: getNativePlayerHtml(videoId, videoProvider, autoplay),
              }
            : {
                uri: src,
                ...(videoProvider === 'YouTube'
                  ? {
                      headers: {
                        Referer: VIDEO_REFERRER,
                      },
                    }
                  : {}),
              }
        }
        style={{
          flex: 1,
        }}
      />
    </View>
  );
}
